import { TagCategoryWithTagsResponse } from "apis/data-contracts";
import TagEditForm, {
  TagFormData,
} from "components/foundation/Tag/TagEditForm";
import useTagsAutoCompleteQuery from "features/tag/hooks/queries/useTagsAutoCompleteQuery";
import TagPageListItem from "pages/tag/TagPageListItem";
import React, { SetStateAction, useContext, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { DirtyContext } from "..";

export type TagChangePayload =
  | {
      type: "add";
      toBe: string;
    }
  | {
      type: "delete";
      asIs: string;
    }
  | {
      type: "rename";
      asIs: string;
      toBe: string;
    };

interface TagEditFormItemProps {
  tagDetail: TagCategoryWithTagsResponse;
  onDuplicate: () => void;
  tags?: { [key: string]: string[] };
  setTags: React.Dispatch<
    React.SetStateAction<{
      [key: string]: string[];
    }>
  >;

  // 카테고리별 태그 변경 이벤트 콜백
  onTagChange?: (tagCategoryUuid: string, change: TagChangePayload) => void;
}

const TagEditFormItem = ({
  tagDetail,
  onDuplicate,
  tags,
  setTags,
  onTagChange,
}: TagEditFormItemProps) => {
  const methods = useForm<TagFormData>({
    defaultValues: {
      tag: "",
    },
  });

  /* 태그 */
  const { data: autoCompleteData } = useTagsAutoCompleteQuery({
    searchTerm: methods.watch("tag"),
  });

  const context = useContext(DirtyContext);

  useEffect(() => {
    if (methods.formState.isDirty) {
      context?.markDirty();
    }
  }, [context, methods.formState.isDirty]);

  return (
    <FormProvider {...methods}>
      <TagPageListItem
        title={`${tagDetail.tagCategoryName}태그`}
        key={tagDetail.tagCategoryUuid}
      >
        <TagEditForm
          tags={tags?.[tagDetail.tagCategoryUuid] || []}
          setTags={(callback: SetStateAction<string[]>) => {
            let result: string[];
            if (typeof callback === "function") {
              result = callback(tags?.[tagDetail.tagCategoryUuid] || []);
            } else {
              result = callback;
            }
            setTags((prev) => ({
              ...prev,
              [tagDetail.tagCategoryUuid]: result,
            }));
          }}
          autocomplete={autoCompleteData?.data.result.map((i) => ({
            label: i,
            value: i,
          }))}
          placeholder="태그를 추가하세요"
          onDuplicate={onDuplicate}
          // 여기부터 태그 변경 콜백 연결
          onAddTag={(tagName) =>
            onTagChange?.(tagDetail.tagCategoryUuid, {
              type: "add",
              toBe: tagName,
            })
          }
          onDeleteTag={(tagName) =>
            onTagChange?.(tagDetail.tagCategoryUuid, {
              type: "delete",
              asIs: tagName,
            })
          }
          onRenameTag={(oldTag, newTag) =>
            onTagChange?.(tagDetail.tagCategoryUuid, {
              type: "rename",
              asIs: oldTag,
              toBe: newTag,
            })
          }
        />
      </TagPageListItem>
    </FormProvider>
  );
};

export default TagEditFormItem;
